import { ArticleCard, Box, Button, EventCard, Flex } from '@cowsquare/design';
import { Link } from 'gatsby';
import gql from 'graphql-tag';
import * as R from 'ramda';
import React from 'react';
import { Query } from 'react-apollo';
import { Helmet } from 'react-helmet';
import styled, { css } from 'styled-components';
import { useAdsQuery } from '../../../utils/hooks';
import { isExpired } from '../../../utils/isExpired';
import takeMap from '../../../utils/takeMap';
import { Advertisements } from '../../Advertisements';
import { getItemProps as getArticleCardProps } from '../../ArticleCardList';
import { getItemProps as getEventCardProps } from '../../EventCardList';
import { Section } from '../../Section';
import { Title } from '../../Title';

const ListFlex = styled(Flex)`
  & > :last-child {
    margin: 0;
  }
`;

const EventCardBox = styled(Box).attrs({
  mb: [5, 0],
  mr: [0, 5],
  width: [1, 1 / 3],
})`
  overflow: hidden;
  ${props =>
    props.expired &&
    css`
      opacity: 0.5;
    `};
`;

const GET_MEMBER_ONLY_HOME_DATA = gql`
  query getMemberOnlyHomeData {
    events: hkdutest_hkdu_events(limit: 4, order_by: { date: desc }) {
      id
      name
      start_time
      end_time
      date
      content
      location
      tags {
        tag {
          name
        }
      }
    }
    news: hkdutest_members_news(limit: 6, order_by: { created_at: desc }) {
      id
      author {
        name
        email
      }
      name
      content
      cover_file {
        link
      }
      tags {
        tag {
          name
        }
      }
      created_at
    }
    materials: hkdutest_member_knowledge_materials(limit: 6, order_by: { created_at: desc }) {
      id
      author {
        name
        email
      }
      name
      content
      cover_file {
        link
      }
      tags {
        tag {
          name
        }
      }
      created_at
    }
  }
`;

const Home = ({ uri }) => {
  const { ads } = useAdsQuery(uri);
  return (
    <Query query={GET_MEMBER_ONLY_HOME_DATA}>
      {({ data }) => {
        const events = R.propOr([], 'events', data);
        const news = R.propOr([], 'news', data);
        const materials = R.propOr([], 'materials', data);

        return (
          <>
            <Helmet>
              <title>Home - HKDU Members Only</title>
            </Helmet>
            <Advertisements ads={ads} itemProps={{ height: [150, 300] }} />
            <Section>
              <Flex flexDirection="column">
                <Box px={[5, 0]} py={8}>
                  <Flex flexDirection="column">
                    <Box mb={8}>
                      <Box mb={6}>
                        <Flex justifyContent="space-between" alignItems="center">
                          <Title>HKDU Events</Title>
                          {R.gt(events.length, 3) && (
                            <Button size="small" as={Link} to="/member-only/events">
                              More
                            </Button>
                          )}
                        </Flex>
                      </Box>
                      <ListFlex flexDirection={['column', 'row']}>
                        {takeMap(3, item => (
                          <EventCardBox key={item.id} expired={isExpired(item.date)}>
                            <EventCard
                              as={Link}
                              to={`/member-only/events/${R.propOr('', 'id', item)}`}
                              {...getEventCardProps({
                                item,
                                uri: '/member-only/events',
                              })}
                              height="260px"
                              variant="vertical"
                            />
                          </EventCardBox>
                        ))(events)}
                      </ListFlex>
                    </Box>
                    {/* -------- News -------- */}
                    <Flex flexDirection="column">
                      <Box mb={6}>
                        <Flex justifyContent="space-between" alignItems="center">
                          <Title>News</Title>
                          {R.gt(news.length, 5) && (
                            <Button size="small" as={Link} to="/member-only/news">
                              More
                            </Button>
                          )}
                        </Flex>
                      </Box>
                      <ListFlex flexDirection="column">
                        {takeMap(5, item => (
                          <Box key={item.id} mb={5}>
                            <ArticleCard
                              as={Link}
                              to={`/member-only/news/${R.propOr('', 'id', item)}`}
                              {...getArticleCardProps({
                                item,
                                uri: '/member-only/news',
                              })}
                              height={['389px', '246px']}
                            />
                          </Box>
                        ))(news)}
                      </ListFlex>
                    </Flex>
                    {/* -------- Knowledge Materials -------- */}
                    <Box mt={8}>
                      <Flex flexDirection="column">
                        <Box mb={6}>
                          <Flex justifyContent="space-between" alignItems="center">
                            <Title>Knowledge Materials</Title>
                            {R.gt(materials.length, 5) && (
                              <Button size="small" as={Link} to="/member-only/knowledge-materials">
                                More
                              </Button>
                            )}
                          </Flex>
                        </Box>
                        <ListFlex flexDirection="column">
                          {takeMap(5, item => (
                            <Box key={item.id} mb={5}>
                              <ArticleCard
                                as={Link}
                                to={`/member-only/knowledge-materials/${R.propOr('', 'id', item)}`}
                                {...getArticleCardProps({
                                  item,
                                  uri: '/member-only/knowledge-materials',
                                })}
                                height={['389px', '246px']}
                              />
                            </Box>
                          ))(materials)}
                        </ListFlex>
                      </Flex>
                    </Box>
                  </Flex>
                </Box>
              </Flex>
            </Section>
          </>
        );
      }}
    </Query>
  );
};

export default Home;
